// src/store/slices/todoCategorySlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import todoCategoryApi from 'api/todoCategoryApi';

export const fetchCategories = createAsyncThunk(
  'todoCategory/fetchCategories',
  async (_, { rejectWithValue }) => { // no page/pageSize/search params needed
    try {
      const response = await todoCategoryApi.getAll();
      return response;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || 'Failed to load categories'
      );
    }
  }
);

export const createCategory = createAsyncThunk(
  'todoCategory/createCategory',
  async (data, { rejectWithValue }) => {
    try {
      const response = await todoCategoryApi.create(data);
      if (!response.success) throw new Error(response.message || 'Create failed');
      return response.data;
    } catch (err) {
     const message =
        err?.response?.data?.message || // 🔥 THIS IS YOUR CASE
        err?.response?.data?.error ||
        err.message ||
        "Failed to create category";

      return rejectWithValue(message);
    }
  }
);

export const updateCategory = createAsyncThunk(
  'todoCategory/updateCategory',
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const response = await todoCategoryApi.update(id, data);
      if (!response.success) throw new Error(response.message || 'Update failed');
      return response.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || // 🔥 THIS IS YOUR CASE
        err?.response?.data?.error ||
        err.message ||
        "Failed to create category";

      return rejectWithValue(message);
    }
  }
);

export const deleteCategory = createAsyncThunk(
  'todoCategory/deleteCategory',
  async (id, { rejectWithValue }) => {
    try {
      await todoCategoryApi.delete(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to delete category');
    }
  }
);

export const exportCategories = createAsyncThunk(
  'todoCategory/exportCategories',
  async (_, { rejectWithValue }) => {
    try {
      const blob = await todoCategoryApi.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `todo-categories-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      return rejectWithValue('Export failed');
    }
  }
);

const todoCategorySlice = createSlice({
  name: 'todoCategory',
  initialState: {
    categories: [],           // tree structure
    isLoading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH
      .addCase(fetchCategories.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.isLoading = false;
        state.categories = action.payload.data || [];
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE – add to root (or you can add logic to insert under parent)
      .addCase(createCategory.fulfilled, (state, action) => {
        state.categories.push(action.payload);
      })

      // UPDATE – recursive tree update
      .addCase(updateCategory.fulfilled, (state, action) => {
        const updateTree = (nodes) =>
          nodes.map((node) =>
            node.id === action.payload.id
              ? action.payload
              : {
                  ...node,
                  children: node.children ? updateTree(node.children) : [],
                }
          );
        state.categories = updateTree(state.categories);
      })

      // DELETE – recursive tree remove
      .addCase(deleteCategory.fulfilled, (state, action) => {
        const removeFromTree = (nodes) =>
          nodes
            .filter((node) => node.id !== action.payload)
            .map((node) => ({
              ...node,
              children: node.children ? removeFromTree(node.children) : [],
            }));
        state.categories = removeFromTree(state.categories);
      })

      // EXPORT
      .addCase(exportCategories.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearError } = todoCategorySlice.actions;
export default todoCategorySlice.reducer;